import { useMemo } from 'react';

import type { PatternDesignOptimisation } from '~api/services';
import { useTableStore } from '~store';
import { applyFilters } from '~utils';
import { generateFilterConfigs } from '../utils';

type UseFilteredOptimisationsResult = {
  /** Filtered optimisations based on current table filter state */
  filteredOptimisations: PatternDesignOptimisation[];
  /** Filter configurations generated from the data */
  filterConfigs: ReturnType<typeof generateFilterConfigs>;
};

/**
 * Hook to filter optimisations based on the current table filter state.
 * Reusable across BlastDetailsIndex and BlastOptimiser pages.
 *
 * @param optimisations - Array of optimisations to filter
 * @returns Filtered optimisations and filter configurations
 */
const useFilteredOptimisations = (optimisations: PatternDesignOptimisation[]): UseFilteredOptimisationsResult => {
  const filtering = useTableStore((state) => state.filtering);

  // Generate filter configs based on actual data distribution
  const filterConfigs = useMemo(() => generateFilterConfigs(optimisations), [optimisations]);

  // Apply filters to optimisations
  const filteredOptimisations = useMemo(
    () => applyFilters(optimisations, filtering, filterConfigs),
    [optimisations, filtering, filterConfigs],
  );

  return { filteredOptimisations, filterConfigs };
};

export { useFilteredOptimisations };
